import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

const getCoords = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 5000 });
});

export function useSos(enabled) {
  const [sos, setSos] = useState(null);
  const [limits, setLimits] = useState(null);
  const [limitMsg, setLimitMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [holding, setHolding] = useState(0);
  const timer = useRef(null);

  const refresh = useCallback(() => api.get("/sos/active").then((r) => setSos(r.data)).catch((e) => console.warn("SOS status unavailable", e)), []);
  const refreshLimits = useCallback(() => api.get("/sos/limits").then((r) => setLimits(r.data)).catch((e) => console.warn("SOS limits unavailable", e)), []);

  useEffect(() => { if (enabled) { refresh(); refreshLimits(); } }, [enabled, refresh, refreshLimits]);
  useEffect(() => {
    if (!sos) return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [sos, refresh]);

  const trigger = useCallback(async (note, onDone) => {
    setBusy(true);
    const coords = await getCoords();
    try {
      const { data } = await api.post("/sos", { note, ...coords });
      setSos(data); refreshLimits(); onDone?.();
    } catch (err) {
      const msg = formatError(err.response?.data?.detail);
      if (err.response?.status === 429) { setLimitMsg(msg); refreshLimits(); } else toast.error(msg);
    } finally { setBusy(false); setHolding(0); }
  }, [refreshLimits]);

  const startHold = (onComplete) => {
    let p = 0;
    timer.current = setInterval(() => {
      p += 100 / 30;
      setHolding(p);
      if (p >= 100) { clearInterval(timer.current); onComplete(); }
    }, 100);
  };
  const endHold = () => { clearInterval(timer.current); if (holding < 100) setHolding(0); };

  const resolve = async () => {
    setBusy(true);
    try { await api.post(`/sos/${sos.id}/resolve`); setSos(null); refreshLimits(); toast.success("Glad you're okay. We've let your family know."); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return { sos, limits, limitMsg, setLimitMsg, busy, holding, trigger, startHold, endHold, resolve };
}
